import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Paper,
  Grid,
  Box,
  Stack,
  Link,
} from '@mui/material';
import {
  AccountBalance,
  LocationOn,
  Phone,
  Email,
  Language,
  Business,
  Description,
  Flag
} from '@mui/icons-material';
import { apiClient } from '../api/apiClient';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import '../styles/map.css';

// Fix für die Marker-Icons in Leaflet
const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = defaultIcon;

const InfoTile = ({ icon, label, value, isLink }) => (
  <Paper 
    elevation={1} 
    sx={{ 
      p: { xs: 1.75, md: 3 },
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 0.75, md: 1.5 },
      backgroundColor: '#fff',
      borderRadius: 2,
      '&:hover': {
        boxShadow: 2
      }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {React.cloneElement(icon, { color: "primary", sx: { fontSize: 24 } })}
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
    <Typography variant="body1" sx={{ pl: { xs: 0, md: 4 }, wordBreak: 'break-word' }}>
      {isLink && value ? (
        <Link href={value} target="_blank" rel="noopener noreferrer">
          {value}
        </Link>
      ) : (
        value || '-'
      )}
    </Typography>
  </Paper>
);

const ContentSection = ({ icon, title, children }) => (
  <Paper 
    elevation={1}
    sx={{ 
      p: { xs: 2, md: 3 },
      borderRadius: 2,
      '&:hover': {
        boxShadow: 2
      }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      {React.cloneElement(icon, { color: "primary", sx: { fontSize: 24 } })}
      <Typography variant="h6">
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

const BUNDESLAENDER = {
  "BWB": "Baden-Württemberg",
  "FSB": "Bayern",
  "BLN": "Berlin",
  "BRB": "Brandenburg",
  "BRE": "Bremen",
  "HAM": "Hamburg",
  "HES": "Hessen",
  "MVP": "Mecklenburg-Vorpommern",
  "NSB": "Niedersachsen",
  "NRW": "Nordrhein-Westfalen",
  "RPF": "Rheinland-Pfalz",
  "SAA": "Saarland",
  "FSS": "Sachsen",
  "SAN": "Sachsen-Anhalt",
  "SHS": "Schleswig-Holstein",
  "FST": "Thüringen",
  "BRD": "Bundesrepublik Deutschland"
};

const GerichtDetail = () => {
  const { id } = useParams();
  const [gericht, setGericht] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGericht = async () => {
      try {
        const response = await apiClient.get(`/gerichte/${id}`);
        setGericht(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Fehler beim Laden des Gerichts:', error);
        setLoading(false);
      }
    };

    fetchGericht();
  }, [id]);

  if (loading) {
    return <Typography>Lade Gericht...</Typography>;
  }

  if (!gericht) {
    return <Typography>Gericht nicht gefunden.</Typography>;
  }

  // Prüfe, ob Koordinaten vorhanden sind
  const hasCoordinates = gericht.latitude && gericht.longitude;

  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto', px: { xs: 1.5, sm: 2, md: 0 }, py: { xs: 2, md: 4 } }}>
      <Stack spacing={{ xs: 2, md: 3 }}>
        {/* Hauptinformationen */}
        <Grid container spacing={{ xs: 1.5, md: 3 }}>
          <Grid item xs={12} md={4}>
            <InfoTile
              icon={<AccountBalance />}
              label="Bezeichnung"
              value={gericht.bezeichnung}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoTile
              icon={<Flag />}
              label="Bundesland"
              value={BUNDESLAENDER[gericht.bundesland] || gericht.bundesland}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoTile
              icon={<Business />}
              label="Instanz"
              value={gericht.instanz}
            />
          </Grid>
        </Grid>

        {/* Kontaktinformationen */}
        <Grid container spacing={{ xs: 1.5, md: 3 }}>
          <Grid item xs={12} md={6}>
            <InfoTile
              icon={<Phone />}
              label="Kontakt"
              value={gericht.kontakt}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoTile
              icon={<Email />}
              label="Pressestelle"
              value={gericht.pressestelle}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoTile
              icon={<Language />}
              label="Homepage"
              value={gericht.homepage}
              isLink={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoTile
              icon={<LocationOn />}
              label="Ort"
              value={gericht.ort}
            />
          </Grid>
        </Grid>

        {/* Adressen */}
        {(gericht.hausanschrift || gericht.postanschrift) && (
          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {gericht.hausanschrift && (
              <Grid item xs={12} md={6}>
                <ContentSection icon={<LocationOn />} title="Hausanschrift">
                  <Typography 
                    variant="body1" 
                    component="div"
                    dangerouslySetInnerHTML={{ __html: gericht.hausanschrift }}
                  />
                </ContentSection>
              </Grid>
            )}
            {gericht.postanschrift && (
              <Grid item xs={12} md={6}>
                <ContentSection icon={<LocationOn />} title="Postanschrift">
                  <Typography 
                    variant="body1" 
                    component="div"
                    dangerouslySetInnerHTML={{ __html: gericht.postanschrift }}
                  />
                </ContentSection>
              </Grid>
            )}
          </Grid>
        )}

        {/* Bezirk */}
        {gericht.bezirk && (
          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            <Grid item xs={12}>
              <ContentSection icon={<Description />} title="Zuständigkeitsbezirk">
                <Typography variant="body1">
                  {gericht.bezirk}
                </Typography>
              </ContentSection>
            </Grid>
          </Grid>
        )}

        {/* OpenStreetMap Integration */}
        {hasCoordinates && (
          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            <Grid item xs={12}>
              <ContentSection icon={<LocationOn />} title="Standort">
    <Box className="map-container">
                  <MapContainer
                    center={[gericht.latitude, gericht.longitude]}
                    zoom={15}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[gericht.latitude, gericht.longitude]}>
                      <Popup>
                        <strong>{gericht.bezeichnung}</strong><br />
                        {gericht.hausanschrift?.replace(/<br\/?>/g, '\n')}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Koordinaten: {gericht.latitude.toFixed(6)}, {gericht.longitude.toFixed(6)}
                </Typography>
              </ContentSection>
            </Grid>
          </Grid>
        )}
      </Stack>
    </Box>
  );
};

export default GerichtDetail; 